import { Box, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import type { DashboardSummary } from '@/lib/api/dashboard';

interface Props {
  alerts: DashboardSummary['alerts'];
}

/**
 * 활성 경보 요약 — 출근 직후 가장 먼저 보는 카드.
 * 건수만이 아니라 상위 대상자를 바로 노출해, 목록 화면을 거치지 않고 조치로 넘어가게 한다.
 */
export function AlertSummaryCard({ alerts }: Props) {
  const navigate = useNavigate();

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            활성 경보
          </Typography>
          <Box sx={{ flex: 1 }} />
          {alerts.critical > 0 && (
            <Chip size="small" color="error" label={`긴급 ${alerts.critical}`} />
          )}
          {alerts.warning > 0 && (
            <Chip size="small" color="warning" label={`일반 ${alerts.warning}`} />
          )}
          {alerts.total === 0 && <Chip size="small" label="없음" variant="outlined" />}
        </Stack>

        {alerts.total === 0 ? (
          <Typography variant="body2" color="text.secondary">
            현재 조치가 필요한 경보가 없습니다.
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {alerts.top.map((a) => (
              <Box
                key={`${a.residentId}-${a.type}`}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/residents?detail=${a.residentId}&tab=today`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/residents?detail=${a.residentId}&tab=today`);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Chip
                  size="small"
                  color={a.severity === 'CRITICAL' ? 'error' : 'warning'}
                  label={a.roomNumber ? `${a.roomNumber}호` : '미배정'}
                  sx={{ minWidth: 64 }}
                />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {a.name ?? a.patientCode ?? '(미지정)'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {a.message}
                </Typography>
              </Box>
            ))}
            {alerts.total > alerts.top.length && (
              <Typography
                variant="caption"
                color="primary.main"
                sx={{ pt: 1, cursor: 'pointer' }}
                onClick={() => navigate('/residents?filter=alert')}
              >
                외 {alerts.total - alerts.top.length}건 더 보기 →
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
